import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import { Field, Form } from 'redux-form'
import styled, { css } from 'styled-components'
import uniqueId from 'lodash/uniqueId'
import { Input } from 'components/common/FormElements/Input'
import Button from 'components/common/Button/Button'
import { A } from 'components/common/Link/Link'
import Text from 'components/common/Text/Text'
import { SearchInputFormattedOption } from './components/SearchInputFormattedOption'
import { getApiErrorDescr, getApiErrorBrands } from './helpers'
import Theme from '../Theme/Theme'

const SavedLocationSearchForm = ({
  botStoreLocation,
  compact,
  handleSubmit,
  model,
  buttonText,
  getSuggestionValue,
  handleOptionsFetch,
  handleFormSpecificChooseAddressSuggestion,
  handleBOTStoreLocationSearch,
  setKeypressScopeRef,
  apiError,
  errorsOnTop,
  shouldShowApiError,
  hasBrands,
  quickApptIsEmpty,
  name,
  useMyLocationClicked,
  setUseMyLocationClicked
}) => {
  const inputField =
    <SearchFormWrapper>
      <InputFieldWrapper compact={ compact }>
        <Field
          { ...model.locationSearch }
          uid={ name }
          full
          component={ Input }
          maxLength={ 100 }
          getSuggestionValue={ getSuggestionValue }
          formatSuggestion={ SearchInputFormattedOption }
          onChooseSuggestion={ handleFormSpecificChooseAddressSuggestion }
          onFetchSuggestions={ handleOptionsFetch } />
      </InputFieldWrapper>
      <SubmitWrapper>
        <CustomButton
          data-at-search-location-button
          full="mobile"
          narrow
          onClick={ botStoreLocation ? handleBOTStoreLocationSearch : () => {
            useMyLocationClicked && setUseMyLocationClicked(false)
          } }>{ buttonText }
        </CustomButton>
      </SubmitWrapper>
    </SearchFormWrapper>

  return (
    <Fragment>
      {
        botStoreLocation ?
          <div ref={ setKeypressScopeRef } data-at-store-locator-search-form>{ inputField }</div> :
          <Form ref={ setKeypressScopeRef } data-at-store-locator-search-form onSubmit={ handleSubmit }>
            { inputField }
          </Form>
      }
      {
        (shouldShowApiError || quickApptIsEmpty) &&
        <ErrorsWrapper showOnTop={ errorsOnTop } hasBrands={ hasBrands }>
          <SearchError
            key={ uniqueId() }
            role="alert"
            aria-live="assertive"
            aria-label={ getApiErrorDescr(apiError) }
            centered
            block
            small
            size={ 12 }>
            { getApiErrorDescr(apiError) }
          </SearchError>
          <SisterCompaniesWrapper>
            {
              getApiErrorBrands(apiError)
                .map(({ brandName, logoPath, logoURL }) =>
                  <A key={ logoPath } href={ logoURL }>
                    <SisterCompanyLogo alt={ brandName } src={ logoPath } />
                  </A>
                )
            }
          </SisterCompaniesWrapper>
        </ErrorsWrapper>
      }
    </Fragment>
  )
}

SavedLocationSearchForm.propTypes = {
  apiError: PropTypes.shape({}),
  botStoreLocation: PropTypes.bool,
  buttonText: PropTypes.string,
  compact: PropTypes.bool,
  disableErrors: PropTypes.bool,
  errorsOnTop: PropTypes.bool,
  fieldIsEmpty: PropTypes.bool,
  form: PropTypes.string,
  getSuggestionValue: PropTypes.func,
  handleBOTStoreLocationSearch: PropTypes.func,
  handleFormSpecificChooseAddressSuggestion: PropTypes.func,
  handleOptionsFetch: PropTypes.func,
  handleSubmit: PropTypes.func,
  hasBrands: PropTypes.bool,
  hasFormSyncErrors: PropTypes.bool,
  model: PropTypes.shape({
    locationSearch: PropTypes.shape()
  }),
  name: PropTypes.string,
  onFetchSuggestions: PropTypes.func,
  quickApptIsEmpty: PropTypes.bool,
  searchError: PropTypes.shape({
    code: PropTypes.string,
    description: PropTypes.string
  }),
  setKeypressScopeRef: PropTypes.func,
  setUseMyLocationClicked: PropTypes.func,
  shouldShowApiError: PropTypes.bool,
  submitFailed: PropTypes.bool,
  useMyLocationClicked: PropTypes.bool
}

export default SavedLocationSearchForm

const InputFieldWrapper = styled.div`
  width: 65%;
  padding: 0 10px 10px 0;
`

const SubmitWrapper = styled.div`
  margin-top: 5px;
`

const CustomButton = styled(Button)`
  padding: 7px 16px 8px;
  height: 2.4em;
  white-space: nowrap;
`

const ErrorsWrapper = styled.div`
  position: ${props => props.showOnTop ? 'absolute' : 'initial'};
  ${props => props.showOnTop && css`
    position: absolute;
    top: 60px;
    left: 58px;
    display: flex;
    flex-direction: column;
    align-items: center;
    background: ${Theme.colors.white};
    width: 250px;
  `}
`

const SisterCompaniesWrapper = styled.div`
  margin: 5px -10px 0 -10px;
  display: flex;
  justify-content: center;
`

const SisterCompanyLogo = styled.img`
  display: inline-block;
  margin: 10px;
`

const SearchError = styled(Text)`
  display: inline-block;
  padding-top: 20px;
  max-width: 230px;
`

const SearchFormWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`
